import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    renderSceneDesignsToDataUrls,
    TemplateBundleNotLoadedError,
} from '../rendering/scene-design-render';
import { normalizeAspectRatio } from '../rendering/scene-design-tile-strip';
import { Tool } from '../tools/Tool';
import type { EnhancedToolResult, ImageContent, TextContent, ToolExecutionContext } from '../types';

import { formatSceneDesignStructure } from './sceneNarration';
import { getBrowsableSceneDesigns, PREVIEW_SCENE_DESIGNS_CAP } from './sceneDesignBrowsing';

export const previewSceneDesignsSchema = z.object({
    ids: z
        .array(z.string())
        .describe(
            `The scene_design_ids to preview, chosen from ${TOOL_NAMES.listSceneDesigns}. ` +
                `Pass at most ${PREVIEW_SCENE_DESIGNS_CAP}; if you pass more, only ` +
                `the first ${PREVIEW_SCENE_DESIGNS_CAP} are rendered.`,
        ),
});
export type PreviewSceneDesignsArgs = z.infer<typeof previewSceneDesignsSchema>;

/**
 * Renders preview images for a small set of the current template's scene designs and returns each
 * one's structural design context (its asset slots) alongside the image. Step 2 of the two-step
 * flow — call list_scene_designs first, then preview a few relevant ids. Rendering is expensive
 * (offscreen render + asset loading), so the preview count is capped (PREVIEW_SCENE_DESIGNS_CAP).
 */
export class PreviewSceneDesignsTool extends Tool<typeof previewSceneDesignsSchema> {
    protected override readonly schema = previewSceneDesignsSchema;
    protected override readonly toolName = TOOL_NAMES.previewSceneDesigns;
    protected override readonly toolDescription =
        "Render preview images for a few of the current template's scene designs and " +
        "get each one's structure (its asset slots) so you can see how it looks AND " +
        'how it is built — reference for matching the template. This is step 2 of 2: ' +
        `first call ${TOOL_NAMES.listSceneDesigns} to scan the catalog, then pass a few (at most ` +
        `${PREVIEW_SCENE_DESIGNS_CAP}) relevant scene_design_ids here. Rendering is ` +
        'expensive, so preview only the designs you actually want to reference, not ' +
        'the whole catalog. Scoped to the current template and aspect ratio.';

    public override getToolDisplayName(): string {
        return 'Previewing scene designs';
    }

    public get statusIcon(): string {
        return 'grid_view';
    }

    public async execute(
        args: PreviewSceneDesignsArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const templateId = this.luminary.template?.id;
        if (!templateId) {
            return {
                result: 'Error: no template is loaded, so there are no scene designs to preview.',
                isSuccess: false,
            };
        }

        if (!args.ids.length) {
            return {
                result:
                    `Error: no scene design ids were provided. Call ${TOOL_NAMES.listSceneDesigns} to choose a ` +
                    'few, then pass their ids here.',
                isSuccess: false,
            };
        }

        const state = this.getState();
        const aspectRatio = normalizeAspectRatio(this.luminary.aspectRatio);
        const designsById = new Map(
            getBrowsableSceneDesigns(state, templateId, aspectRatio).map((design) => [
                design.id,
                design,
            ]),
        );

        const unknownIds = args.ids.filter((id) => !designsById.has(id));
        if (unknownIds.length) {
            return {
                result:
                    `Error: unknown scene design id(s): ${unknownIds.join(', ')}. ` +
                    `Valid ids for this template: ${[...designsById.keys()].join(', ')}. ` +
                    `Call ${TOOL_NAMES.listSceneDesigns} to see the catalog.`,
                isSuccess: false,
            };
        }

        const idsToRender = args.ids.slice(0, PREVIEW_SCENE_DESIGNS_CAP);
        const truncated = args.ids.length > idsToRender.length;

        let renders;
        try {
            renders = await renderSceneDesignsToDataUrls({
                templateId,
                aspectRatio,
                sceneDesignIds: idsToRender,
                getState: this.getState,
                describe: true,
            });
        } catch (error) {
            if (error instanceof TemplateBundleNotLoadedError) {
                return {
                    result:
                        `Error: the template "${templateId}" bundle is not loaded, so its scene ` +
                        'designs cannot be previewed right now.',
                    isSuccess: false,
                };
            }
            throw error;
        }

        const content: (TextContent | ImageContent)[] = [];
        let unavailable = 0;
        for (const render of renders) {
            const name = designsById.get(render.sceneDesignId)?.display_name?.trim();
            content.push({
                type: 'text',
                text: `Design ${name ? `"${name}" — ` : ''}${render.sceneDesignId}:`,
            });
            if (render.dataUrl) {
                content.push({ type: 'image_url', image_url: { url: render.dataUrl } });
            } else {
                unavailable++;
                content.push({
                    type: 'text',
                    text: '(preview image unavailable — the render failed)',
                });
            }
            if (render.description) {
                content.push({
                    type: 'text',
                    text: formatSceneDesignStructure(render.description, this.luminary),
                });
            }
        }

        const summary = [
            `Previewed ${renders.length} scene design${renders.length === 1 ? '' : 's'}.`,
        ];
        if (truncated) {
            summary.push(`Requested ${args.ids.length}; capped at ${PREVIEW_SCENE_DESIGNS_CAP}.`);
        }
        if (unavailable) {
            summary.push(
                `${unavailable} preview image${unavailable === 1 ? '' : 's'} unavailable (render failed).`,
            );
        }

        return { result: summary.join(' '), injectAsUserMessage: content, isSuccess: true };
    }
}
